<script setup>
import { computed } from 'vue';
import { fromUnixTime, isToday, isYesterday, isSameYear, format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMessageContext } from '../provider.js';

const { content, createdAt } = useMessageContext();

const readableTime = computed(() => {
  const ts = createdAt.value;
  if (!ts) return '';
  const date = fromUnixTime(ts);
  const time = format(date, 'h:mm a');
  if (isToday(date)) return `Hoy ${time}`;
  if (isYesterday(date)) return `Ayer ${time}`;
  if (isSameYear(date, new Date())) return `${format(date, "d 'de' MMM", { locale: es })} ${time}`;
  return `${format(date, "d 'de' MMM yyyy", { locale: es })} ${time}`;
});

// Color del dot según el tipo de evento (detectado por emoji o palabra clave).
const dotColor = computed(() => {
  const c = content?.value || '';
  if (/✓|resolvió|cerró|cerrada/i.test(c)) return 'bg-emerald-500';
  if (/reabr/i.test(c)) return 'bg-orange-400';
  if (/asign|tomó/i.test(c)) return 'bg-amber-400';
  if (/etiqueta|label/i.test(c)) return 'bg-rose-400';
  if (/equipo|team/i.test(c)) return 'bg-blue-400';
  if (/embudo|etapa|movió|pipeline|stage|🔄/i.test(c)) return 'bg-cyan-400';
  if (/edit|editó|✏️/i.test(c)) return 'bg-purple-400';
  if (/lead|deal|📋/i.test(c)) return 'bg-emerald-400';
  if (/prioridad|priority/i.test(c)) return 'bg-yellow-400';
  if (/sla/i.test(c)) return 'bg-red-400';
  return 'bg-slate-400';
});
</script>

<template>
  <div class="c3s4-activity-pill" data-bubble-name="activity">
    <div class="c3s4-activity-pill-inner">
      <span class="c3s4-activity-dot" :class="dotColor" />
      <span class="c3s4-activity-time">{{ readableTime }}</span>
      <span class="c3s4-activity-sep" />
      <span v-dompurify-html="content" class="c3s4-activity-text" :title="content" />
    </div>
  </div>
</template>

<style scoped>
/* C3-S4 · Activity pill estilo Apple · centered, sutil, con timestamp destacado */
.c3s4-activity-pill {
  display: flex;
  justify-content: center;
  margin: 8px 0;
  min-width: 0;
}
.c3s4-activity-pill-inner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px;
  background: rgba(255,255,255,0.04);
  border: 0.5px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  font-size: 10.5px;
  color: rgba(255,255,255,0.65);
  font-weight: 500;
  font-style: normal;
  max-width: 100%;
  transition: background 0.22s, border-color 0.22s;
}
.c3s4-activity-pill-inner:hover {
  background: rgba(255,255,255,0.06);
  border-color: rgba(255,255,255,0.16);
}
.c3s4-activity-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 6px currentColor;
}
.c3s4-activity-time {
  font-size: 9.5px;
  color: rgba(255,255,255,0.40);
  font-weight: 700;
  font-feature-settings: "tnum";
  flex-shrink: 0;
}
.c3s4-activity-sep {
  width: 0.5px;
  height: 12px;
  background: rgba(255,255,255,0.12);
  flex-shrink: 0;
}
.c3s4-activity-text {
  font-size: 10.5px;
  color: rgba(255,255,255,0.75);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.c3s4-activity-text :deep(strong) {
  color: rgba(255,255,255,0.95);
  font-weight: 700;
}
</style>
